import {Component, Inject, OnDestroy} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {FlexModule} from '@angular/flex-layout/flex';
import {Subject, Subscription} from 'rxjs';
import {Moment} from 'moment';
import {
  DatepickerValue,
  duplicatePersonMessage,
  findDuplicatePerson,
  KybIndividualTrusteeDialogData,
  KybIndividualTrusteeDialogResult,
  ComparableIdentity,
} from '@portal-workspace/grow-shared-library';
import {NameComponent} from '../name-component/name.component';
import {DatepickerComponent} from '../datepicker-component/datepicker.component';
import {MarkDirective} from '../../directives/mark-as-dirty.directive';

@Component({
  templateUrl: './kyb-individual-trustee.dialog.html',
  styleUrls: ['./kyb-individual-trustee.dialog.scss'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, FlexModule, MarkDirective, NameComponent, DatepickerComponent],
})
export class KybIndividualTrusteeDialog implements OnDestroy {

  markObservable: Subject<boolean> = new Subject<boolean>();

  formControlFirstName: FormControl<string | null>;
  formControlMiddleName: FormControl<string | null>;
  formControlLastName: FormControl<string | null>;
  formControlDob: FormControl<DatepickerValue>;
  formGroup: FormGroup;

  /** Set when this person is already on the application; blocks the save. */
  duplicateError: string | null = null;
  /**
   * Set when the name matches someone whose date of birth is unknown, so it could be a different
   * person. The next Save goes through.
   */
  duplicateWarning: string | null = null;

  subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: KybIndividualTrusteeDialogData,
    private matDialogRef: MatDialogRef<KybIndividualTrusteeDialog, KybIndividualTrusteeDialogResult>,
  ) {
    this.formControlFirstName = formBuilder.control(data?.firstName ?? null, [Validators.required]);
    this.formControlMiddleName = formBuilder.control(data?.middleName ?? null);
    this.formControlLastName = formBuilder.control(data?.lastName ?? null, [Validators.required]);
    this.formControlDob = formBuilder.control(data?.dob ?? null, [Validators.required]);
    this.formGroup = formBuilder.group({
      firstName: this.formControlFirstName,
      middleName: this.formControlMiddleName,
      lastName: this.formControlLastName,
      dob: this.formControlDob,
    });
    // A changed name or date of birth describes someone else, so neither the block nor a warning the
    // broker has already accepted may carry over to it.
    const identityControls: AbstractControl[] = [this.formControlFirstName, this.formControlLastName, this.formControlDob];
    for (const control of identityControls) {
      this.subscriptions.push(control.valueChanges.subscribe(() => {
        this.duplicateError = null;
        this.duplicateWarning = null;
      }));
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onCancel(): void {
    this.matDialogRef.close(undefined);
  }

  onSubmit(): void {
    if (this.formGroup.invalid) {
      this.markObservable.next(true);
      return;
    }
    if (!this.acceptDuplicateCheck()) {
      return;
    }
    this.matDialogRef.close({
      firstName: this.formControlFirstName.value as string,
      middleName: this.formControlMiddleName.value ?? null,
      lastName: this.formControlLastName.value as string,
      dob: this.formControlDob.value as Moment,
    });
  }

  /**
   * Whether the save may proceed. An exact match never can; a name-only one holds the first Save to
   * show the warning and lets the second through.
   */
  private acceptDuplicateCheck(): boolean {
    this.duplicateError = null;
    const candidate: ComparableIdentity = {
      givenName: this.formControlFirstName.value,
      middleName: this.formControlMiddleName.value,
      surname: this.formControlLastName.value,
      dateOfBirth: (this.formControlDob.value as Moment | null)?.format('YYYY-MM-DD') ?? null,
    };
    const hit = findDuplicatePerson(candidate, this.data.existingPeople ?? []);
    if (!hit) {
      this.duplicateWarning = null;
      return true;
    }
    if (hit.match === 'exact') {
      this.duplicateError = duplicatePersonMessage(candidate, hit);
      return false;
    }
    if (this.duplicateWarning) {
      return true;
    }
    this.duplicateWarning = duplicatePersonMessage(candidate, hit);
    return false;
  }
}
